import React, {
  useRef,
} from 'react';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  BillingProviderRuleDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import Tag from 'core/components/Tag';
import DragNDropIcon from 'core/icons/dragNDrop';
import {
  DeleteConfigCell,
} from 'views/EMR/Settings/BillingConfigurations/columns';
import Tooltip from 'core/components/Tooltip';

export function PayerSummary({ payerEntrySummary }: BillingProviderRuleDTO) {
  const ref = useRef<HTMLDivElement>(null);
  return isNil(payerEntrySummary)
    ? <span className="font-medium text-gray-400">&lt;Match Any&gt;</span>
    : (
      <div className="flex font-medium items-center gap-1 w-full">
        <Tooltip title={payerEntrySummary?.name} auto ref={ref}>
          <span className="after:inline-block after:relative last:after:!hidden !truncate" ref={ref}>
            {payerEntrySummary?.name}
          </span>
        </Tooltip>
        <Tag
          tagName={payerEntrySummary?.payerId ?? ''}
          defaultColor="!bg-gray-200 !text-gray-700"
          tooltip={`Payer Id: ${payerEntrySummary?.payerId ?? ''}`}
        />
      </div>
    );
}

type RuleSummaryProps = {
  name?: string | null;
};

export function RuleSummary({ name }: RuleSummaryProps) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <Tooltip title={name ?? ''} auto ref={ref}>
      <div ref={ref} className="truncate w-full">
        {isNil(name)
          ? <span className=" text-gray-400">&lt;Match Any&gt;</span>
          : name}
      </div>
    </Tooltip>
  );
}

export function BillingProviderSummary({ billingProviderSummary }: BillingProviderRuleDTO) {
  return (
    <div className="flex font-medium items-center gap-1 w-full">
      <Tag
        tagName={billingProviderSummary?.type?.slice(0, 3)}
        defaultColor="!bg-gray-200 !text-gray-700"
      />
      <Tooltip
        title={(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">{billingProviderSummary?.name}</div>
            <div className="text-gray-500">
              NPI:
              {' '}
              {billingProviderSummary?.npi}
            </div>
            <div className="text-gray-500">
              Tax Id:
              {' '}
              {billingProviderSummary?.taxId}
            </div>
          </div>
        )}
      >
        <div className="w-full truncate flex-1">
          {billingProviderSummary?.name}
          {' ('}
          {billingProviderSummary?.npi}
          {' • '}
          {billingProviderSummary?.taxId}
          )
        </div>
      </Tooltip>
    </div>
  );
}

export const billingProviderRulesColumns: ColumnDef<BillingProviderRuleDTO>[] = [{
  id: 'order',
  accessorKey: 'order',
  columnTitle: 'Order',
  size: 64,
  minSize: 64,
  cell: ({ row: { original: { order = 0 } } }) => (
    <div className="flex gap-1 items-center">
      <DragNDropIcon className="!h-4 !w-4 !text-gray-400" />
      <div className="w-4 h-4 rounded bg-gray-100 text-gray-700 text-center font-semibold">{order + 1}</div>
    </div>
  ),
}, {
  id: 'payerEntryId',
  accessorKey: 'payerEntryId',
  columnTitle: 'Payer',
  meta: { autoSized: true },
  minSize: 150,
  cell: ({ row: { original } }) => (<PayerSummary {...original} />),
}, {
  id: 'serviceFacilityId',
  accessorKey: 'serviceFacilityId',
  columnTitle: 'Service Facility',
  meta: { autoSized: true },
  minSize: 150,
  cell: ({ row: { original: { serivceFacilitySummary } } }) => (
    <RuleSummary name={serivceFacilitySummary?.name} />
  ),
}, {
  id: 'renderingProviderId',
  accessorKey: 'renderingProviderId',
  columnTitle: 'Rendering Provider',
  meta: { autoSized: true },
  minSize: 150,
  cell: ({ row: { original: { renderingProviderSummary } } }) => (
    <RuleSummary name={renderingProviderSummary?.name} />
  ),
}, {
  id: 'billingProviderId',
  accessorKey: 'billingProviderId',
  columnTitle: 'Billing Provider',
  meta: { autoSized: true },
  minSize: 150,
  cell: ({ row: { original } }) => (
    <BillingProviderSummary {...original} />
  ),
}, {
  id: 'actions',
  accessorKey: 'actions',
  columnTitle: '',
  size: 48,
  cell: ({ row: { original: { billingProviderRuleId } } }) => (
    <DeleteConfigCell
      type="billingProviderRule"
      configTitle="Billing Provider Rule"
      payload={{ billingProviderRuleId }}
      canDelete
    />
  ),
}];
